import React, { useEffect, useState } from 'react';
import { User, ChatConversation } from '../../types';
import { api } from '../../services/apiService';
import ConversationView from './ConversationView';

interface Props {
  leadId: string;
  currentUser: User;
}

const LeadDiscussionTab: React.FC<Props> = ({ leadId, currentUser }) => {
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Au mount, lookup ONLY (createIfMissing=false) — on ne crée pas de conv
  // tant que l'utilisateur n'a pas explicitement démarré la discussion. Ça
  // évite la pollution BDD avec des conv vides à chaque consultation de
  // l'onglet.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { id } = await api.chatGetOrCreateForLead(leadId, false);
        if (cancelled) return;
        if (!id) {
          setConversation(null);
          return;
        }
        const list = await api.chatListConversations();
        if (cancelled) return;
        const conv = list.find(c => c.id === id);
        setConversation(conv || null);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [leadId]);

  // Création explicite (déclenchée par le bouton "Démarrer")
  const handleStart = async () => {
    setCreating(true);
    setError(null);
    try {
      const { id } = await api.chatGetOrCreateForLead(leadId, true);
      if (!id) throw new Error('Création échouée');
      const list = await api.chatListConversations();
      const conv = list.find(c => c.id === id);
      if (!conv) throw new Error('Conversation introuvable après création');
      setConversation(conv);
    } catch (err: any) {
      setError(err?.message || 'Erreur de création');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-slate-500 dark:text-[#546a96]">
        Chargement du thread de discussion…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-rose-600 dark:text-rose-300">
        {error}
      </div>
    );
  }

  // État vide — aucune conv n'existe encore pour ce lead
  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 gap-4">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-indigo-500/10 text-indigo-400">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h3 className="text-base font-bold text-slate-900 dark:text-[#e0eaff]">
          Aucune discussion pour ce prospect
        </h3>
        <p className="text-sm text-slate-500 dark:text-[#7f93bc] max-w-sm leading-relaxed">
          Démarrez une discussion contextuelle avec l'équipe à propos de ce prospect.
          Le manager et la personne assignée seront automatiquement ajoutés.
        </p>
        <button
          type="button"
          onClick={handleStart}
          disabled={creating}
          className="orion-modal-primary-btn text-white font-black px-8 py-3 rounded-2xl text-xs uppercase tracking-widest shadow-xl disabled:opacity-50"
        >
          {creating ? 'Création…' : 'Démarrer la discussion'}
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ConversationView
        conversation={conversation}
        currentUser={currentUser}
        onDeleted={() => {
          // Après suppression, on revient à l'état vide "Démarrer la discussion"
          setConversation(null);
        }}
      />
    </div>
  );
};

export default LeadDiscussionTab;
